import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from './firebase';

// Base URL for Gmail API
const API_BASE_URL = 'https://gmail.googleapis.com/gmail/v1';

// Exchange authorization code for tokens
export async function exchangeCodeForTokens(code, redirectUri) {
  try {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const clientSecret = process.env.REACT_APP_GOOGLE_CLIENT_SECRET;
    
    if (!clientId || !clientSecret) {
      throw new Error('Google OAuth credentials are not configured');
    }
    
    console.log('Exchanging code for tokens with redirect URI:', redirectUri);
    
    // Make request to Google's token endpoint
    const tokenResponse = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        code,
        client_id: clientId,
        client_secret: clientSecret,
        redirect_uri: redirectUri,
        grant_type: 'authorization_code',
      }),
    });
    
    console.log('Token response status:', tokenResponse.status);
    
    if (!tokenResponse.ok) {
      let errorMessage = 'Failed to exchange code for tokens';
      try {
        const errorData = await tokenResponse.json();
        errorMessage = `${errorMessage}: ${errorData.error_description || errorData.error || 'Unknown error'}`;
        console.error('Token response error:', errorData);
      } catch (parseError) {
        console.error('Error parsing error response:', parseError);
      }
      throw new Error(errorMessage);
    }
    
    const tokens = await tokenResponse.json();
    console.log('Successfully exchanged code for tokens');
    
    // Add timestamp for token expiration tracking
    tokens.created_at = Date.now();
    
    return tokens;
  } catch (error) {
    console.error('Error exchanging code for tokens:', error);
    throw error;
  }
}

// Refresh access token when it expires
export async function refreshAccessToken(refreshToken) {
  try {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const clientSecret = process.env.REACT_APP_GOOGLE_CLIENT_SECRET;
    
    if (!clientId || !clientSecret) {
      throw new Error('Google OAuth credentials are not configured');
    }
    
    // Make request to Google's token endpoint for refresh
    const tokenResponse = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        client_id: clientId,
        client_secret: clientSecret,
        refresh_token: refreshToken,
        grant_type: 'refresh_token',
      }),
    });
    
    if (!tokenResponse.ok) {
      const errorData = await tokenResponse.json();
      throw new Error(`Failed to refresh token: ${errorData.error_description || errorData.error || 'Unknown error'}`);
    }
    
    const tokens = await tokenResponse.json();
    
    // Update timestamp and keep the refresh token
    tokens.created_at = Date.now();
    tokens.refresh_token = refreshToken; // Keep the refresh token as Google doesn't return it again
    
    return tokens;
  } catch (error) {
    console.error('Error refreshing access token:', error);
    throw error;
  }
}

// Save Gmail tokens to the company profile
export async function saveGmailTokens(companyId, tokens) {
  try {
    if (!companyId) {
      throw new Error('No company ID provided');
    }
    
    const companyRef = doc(db, "companies", companyId);
    await updateDoc(companyRef, {
      googleGmail: tokens
    });
    
    return true;
  } catch (error) {
    console.error('Error saving Gmail tokens:', error);
    throw error;
  }
}

// Get tokens and refresh if needed
async function getValidTokens(companyId) {
  try {
    if (!companyId) {
      throw new Error('No company ID provided');
    }
    
    const companyRef = doc(db, "companies", companyId);
    const companySnap = await getDoc(companyRef);
    
    if (!companySnap.exists()) {
      throw new Error('Company profile not found');
    }
    
    const data = companySnap.data();
    if (!data.googleGmail) {
      throw new Error('Google Gmail not connected');
    }
    
    const tokens = data.googleGmail;
    
    // Check if access token is expired (tokens typically last 1 hour = 3600 seconds)
    const now = Date.now();
    const tokenCreatedAt = tokens.created_at || 0;
    const expiresIn = tokens.expires_in || 3600;
    const expiresAt = tokenCreatedAt + (expiresIn * 1000);
    
    // If token is expired or expiring soon (within 5 minutes), refresh it
    if (now >= expiresAt - 5 * 60 * 1000) {
      console.log('Access token expired or expiring soon, refreshing...');
      
      // Refresh the token
      const newTokens = await refreshAccessToken(tokens.refresh_token);
      
      // Save the new tokens
      await saveGmailTokens(companyId, newTokens);
      
      return newTokens;
    }
    
    return tokens;
  } catch (error) {
    console.error('Error getting valid tokens:', error);
    throw error;
  }
}

// Search Gmail messages
export async function searchGmailMessages(companyId, query, maxResults = 10) {
  try {
    const tokens = await getValidTokens(companyId);
    
    // Build the search query parameter
    const searchQuery = encodeURIComponent(query);
    
    // First get message IDs that match the query
    const url = `${API_BASE_URL}/users/me/messages?q=${searchQuery}&maxResults=${maxResults}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${tokens.access_token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to search Gmail: ${errorData.error?.message || 'Unknown error'}`);
    }
    
    const data = await response.json();
    
    if (!data.messages || data.messages.length === 0) {
      return [];
    }
    
    // Fetch details for each message
    const messagePromises = data.messages.map(async (message) => {
      return getGmailMessageDetails(companyId, message.id, tokens.access_token);
    });
    
    const messageDetails = await Promise.all(messagePromises);
    return messageDetails;
  } catch (error) {
    console.error('Error searching Gmail:', error);
    throw error;
  }
}

// Get details for a specific Gmail message
async function getGmailMessageDetails(companyId, messageId, accessToken) {
  try {
    const url = `${API_BASE_URL}/users/me/messages/${messageId}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to get message details: ${errorData.error?.message || 'Unknown error'}`);
    }
    
    const messageData = await response.json();
    return formatGmailMessage(messageData);
  } catch (error) {
    console.error('Error getting Gmail message details:', error);
    throw error;
  }
}

// Format Gmail message for display
export function formatGmailMessage(message) {
  try {
    if (!message || !message.payload) {
      return null;
    }
    
    // Extract headers
    const headers = message.payload.headers || [];
    const subject = headers.find(h => h.name.toLowerCase() === 'subject')?.value || 'No Subject';
    const from = headers.find(h => h.name.toLowerCase() === 'from')?.value || 'Unknown Sender';
    const to = headers.find(h => h.name.toLowerCase() === 'to')?.value || 'Unknown Recipient';
    const date = headers.find(h => h.name.toLowerCase() === 'date')?.value || '';
    
    // Format the date
    let formattedDate = date;
    try {
      if (date) {
        const dateObj = new Date(date);
        formattedDate = dateObj.toLocaleDateString(undefined, {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        });
      }
    } catch (e) {
      console.warn('Error formatting date:', e);
    }
    
    // Extract body content (text or HTML)
    let body = '';
    let snippet = message.snippet || '';
    
    // Try to get the body content from parts
    if (message.payload.body && message.payload.body.data) {
      // Decode base64 data
      body = atob(message.payload.body.data.replace(/-/g, '+').replace(/_/g, '/'));
    } else if (message.payload.parts) {
      // Look for text/plain or text/html parts
      const textPart = message.payload.parts.find(part => part.mimeType === 'text/plain');
      const htmlPart = message.payload.parts.find(part => part.mimeType === 'text/html');
      
      if (textPart && textPart.body && textPart.body.data) {
        body = atob(textPart.body.data.replace(/-/g, '+').replace(/_/g, '/'));
      } else if (htmlPart && htmlPart.body && htmlPart.body.data) {
        // Convert HTML to plain text for display (simplified version)
        const htmlBody = atob(htmlPart.body.data.replace(/-/g, '+').replace(/_/g, '/'));
        body = htmlBody.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
      }
    }
    
    // If we couldn't extract the body, use the snippet
    if (!body && snippet) {
      body = snippet;
    }
    
    return {
      id: message.id,
      threadId: message.threadId,
      subject,
      from,
      to,
      date: formattedDate,
      snippet,
      body,
      labels: message.labelIds || [],
      unread: message.labelIds ? message.labelIds.includes('UNREAD') : false
    };
  } catch (error) {
    console.error('Error formatting Gmail message:', error);
    return {
      id: message.id || 'unknown',
      subject: 'Error parsing email',
      from: 'Unknown',
      to: 'Unknown',
      date: '',
      snippet: message.snippet || 'Error parsing email content',
      body: 'There was an error processing this email.',
      labels: [],
      unread: false
    };
  }
}

// Find recent unread emails
export async function findRecentUnreadEmails(companyId, maxResults = 5) {
  return searchGmailMessages(companyId, 'is:unread', maxResults);
}

// Find emails from a specific sender
export async function findEmailsFromSender(companyId, sender, maxResults = 5) {
  return searchGmailMessages(companyId, `from:${sender}`, maxResults);
}

// Find emails with a specific subject or keywords
export async function findEmailsBySubject(companyId, subject, maxResults = 5) {
  return searchGmailMessages(companyId, `subject:${subject}`, maxResults);
}

// Find emails related to a specific topic (broader search)
export async function findEmailsByTopic(companyId, topic, maxResults = 5) {
  return searchGmailMessages(companyId, topic, maxResults);
} 